import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { journalApi } from '../../api/journal';
import { formatMoney } from '../../utils/formatMoney';
import { formatDate, monthsAgo, today } from '../../utils/formatDate';
import DataTable from '../../components/DataTable';
import LoadingSpinner from '../../components/LoadingSpinner';
import DatePickerInput from '../../components/DatePickerInput';
import { useReportScreen } from '../../components/ScreenContext';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

type SourceFilter = '' | 'manual' | 'invoice' | 'bank' | 'closing' | 'ai' | 'adjustment';

// Map documentType to source category
function mapDocTypeToSource(docType: string): SourceFilter {
  switch (docType?.toLowerCase()) {
    case 'invoicemade':
    case 'sf':
      return 'invoice';
    case 'invoicereceived':
    case 'sg':
      return 'invoice';
    case 'bankoperation':
    case 'b':
      return 'bank';
    case 'closingentries':
    case 'uz':
      return 'closing';
    case 'openingbalance':
    case 'lik':
      return 'closing';
    case 'offset':
    case 'sk':
      return 'bank';
    case 'customvat':
    case 'accumulatedcosts':
      return 'adjustment';
    default:
      return 'manual';
  }
}

// Source badge colors
function SourceBadge({ source }: { source: string }) {
  const { t } = useTranslation();
  const config: Record<string, { bg: string; text: string; label: string }> = {
    manual: { bg: 'bg-gray-100', text: 'text-gray-700', label: t('journal.manualEntry') },
    invoice: { bg: 'bg-primary-50', text: 'text-primary-700', label: t('journal.invoicePosting') },
    bank: { bg: 'bg-green-50', text: 'text-green-700', label: t('journal.bankOperation') },
    closing: { bg: 'bg-red-50', text: 'text-red-700', label: t('journal.periodClosing') },
    ai: { bg: 'bg-slate-100', text: 'text-slate-600', label: t('journal.aiGenerated') },
    adjustment: { bg: 'bg-amber-50', text: 'text-amber-700', label: t('journal.adjustment') },
  };
  const fallback = { bg: 'bg-gray-100', text: 'text-gray-700', label: t('journal.manualEntry') };
  const c = config[source] ?? fallback;
  return (
    <span className={`inline-block px-1.5 py-0.5 text-xs ${c.bg} ${c.text} rounded font-medium`}>
      {c.label}
    </span>
  );
}

type JournalTab = 'entries' | 'audit-trail';

export default function JournalList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('q') || '';
  const initialFrom = searchParams.get('from') || monthsAgo(3);
  const initialTo = searchParams.get('to') || today();
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [search, setSearch] = useState(initialSearch);
  const debouncedSearch = useDebouncedValue(search, 300);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('');
  const [activeTab, setActiveTab] = useState<JournalTab>('entries');

  const SOURCE_FILTER_OPTIONS: { value: SourceFilter; label: string }[] = [
    { value: '', label: t('journal.allSources') },
    { value: 'manual', label: t('journal.manualEntry') },
    { value: 'invoice', label: t('journal.invoicePosting') },
    { value: 'bank', label: t('journal.bankOperation') },
    { value: 'closing', label: t('journal.periodClosing') },
    { value: 'ai', label: t('journal.aiGenerated') },
    { value: 'adjustment', label: t('journal.adjustment') },
  ];

  const { data: entries, isLoading } = useQuery({
    queryKey: ['journal-entries', from, to, debouncedSearch],
    queryFn: () => journalApi.list({ from, to, q: debouncedSearch || undefined, limit: 200 }),
  });

  // All hooks must be above any early return (Rules of Hooks)
  const filteredEntries = useMemo(() => {
    if (!entries) return [];
    if (!sourceFilter) return entries;
    return entries.filter(e => mapDocTypeToSource(e.documentType) === sourceFilter);
  }, [entries, sourceFilter]);

  const sourceStats = useMemo(() => {
    const stats: Record<string, number> = {};
    for (const e of entries ?? []) {
      const source = mapDocTypeToSource(e.documentType);
      stats[source] = (stats[source] ?? 0) + 1;
    }
    return stats;
  }, [entries]);

  const entryCount = filteredEntries.length;
  const screenFilters = useMemo(() => {
    const f: Record<string, string> = { nuo: from, iki: to };
    if (search) f.paieska = search;
    if (sourceFilter) f.saltinis = sourceFilter;
    return f;
  }, [from, to, search, sourceFilter]);

  const screenPreview = useMemo(() =>
    (filteredEntries).slice(0, 8).map(e =>
      `${e.documentNumber || e.id}: D=${formatMoney(e.totalDebit)} C=${formatMoney(e.totalCredit)} Data=${formatDate(e.date)}${e.content ? ` Turinys: ${e.content.substring(0, 50)}` : ''}${e.personName ? ` Asmuo: ${e.personName}` : ''}`
    ), [filteredEntries]);

  useReportScreen(`Zurnalas (${entryCount} irasu)`, {
    recordCount: entryCount,
    filters: screenFilters,
    preview: screenPreview,
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">{t('journal.title')}</h1>
        <button
          className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
          onClick={() => navigate('/journal/new')}
        >
          {t('journal.newEntry')}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0 -mb-px">
          {([
            { id: 'entries' as const, label: t('journal.allEntries') },
            { id: 'audit-trail' as const, label: t('journal.postingLog') },
          ]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'entries' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-end">
            <DatePickerInput
              label={t('common.from')}
              value={from}
              onChange={setFrom}
              inputClassName="w-36"
            />
            <DatePickerInput
              label={t('common.to')}
              value={to}
              onChange={setTo}
              inputClassName="w-36"
            />
            <div className="min-w-40">
              <label className="block text-xs text-gray-500 mb-1">{t('journal.source')}</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
              >
                {SOURCE_FILTER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-48">
              <label className="block text-xs text-gray-500 mb-1">{t('common.search')}</label>
              <input
                type="text"
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                placeholder={t('common.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Source summary strip */}
          <div className="flex gap-2 flex-wrap">
            {Object.entries(sourceStats).map(([source, count]) => (
              <button
                key={source}
                onClick={() => setSourceFilter(sourceFilter === source ? '' : source as SourceFilter)}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  sourceFilter === source
                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <SourceBadge source={source} />
                <span className="font-medium">{count}</span>
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200">
            <DataTable
              columns={[
                { key: 'date', header: t('common.date'), render: (row) => formatDate(row.date), className: 'whitespace-nowrap' },
                { key: 'documentNumber', header: t('common.docNumber'), className: 'whitespace-nowrap' },
                {
                  key: 'source',
                  header: t('journal.source'),
                  render: (row) => <SourceBadge source={mapDocTypeToSource(row.documentType)} />,
                  className: 'whitespace-nowrap',
                },
                { key: 'content', header: t('common.content'), className: 'max-w-48 truncate' },
                { key: 'personName', header: t('common.person'), render: (row) => row.personName ?? '-' },
                { key: 'totalDebit', header: t('journal.debit'), render: (row) => <span className="num">{formatMoney(row.totalDebit)}</span>, className: 'text-right' },
                { key: 'totalCredit', header: t('journal.credit'), render: (row) => <span className="num">{formatMoney(row.totalCredit)}</span>, className: 'text-right' },
                {
                  key: 'balanced',
                  header: t('journal.balanced'),
                  className: 'text-center',
                  render: (row) => {
                    const balanced = Math.abs(row.totalDebit - row.totalCredit) < 0.005;
                    return (
                      <span className={`text-xs font-medium ${balanced ? 'text-green-600' : 'text-red-600'}`}>
                        {balanced ? t('common.yes') : t('journal.unbalanced')}
                      </span>
                    );
                  },
                },
              ]}
              data={filteredEntries}
              keyFn={(row) => row.id}
              onRowClick={(row) => navigate(`/journal/${row.id}`)}
              emptyMessage={t('journal.noJournalEntriesFound')}
            />
          </div>

          {/* Totals */}
          {filteredEntries.length > 0 && (
            <div className="flex justify-end gap-6 text-sm">
              <div>
                <span className="text-gray-500">{t('journal.totalDebit')}: </span>
                <span className="font-semibold num">{formatMoney(filteredEntries.reduce((a, e) => a + e.totalDebit, 0))}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('journal.totalCredit')}: </span>
                <span className="font-semibold num">{formatMoney(filteredEntries.reduce((a, e) => a + e.totalCredit, 0))}</span>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'audit-trail' && (
        <PostingLogTab entries={entries ?? []} />
      )}

    </div>
  );
}

function PostingLogTab({ entries }: { entries: { id: number; date: string; documentNumber: string; documentType: string; content: string; personName: string | null; totalDebit: number; totalCredit: number }[] }) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">
        {t('journal.auditTrailDescription')}
      </p>
      <div className="bg-white rounded-lg border border-gray-200">
        <DataTable
          columns={[
            { key: 'date', header: t('journal.postedDate'), render: (row) => formatDate(row.date), className: 'whitespace-nowrap' },
            { key: 'documentNumber', header: t('common.docNumber'), className: 'whitespace-nowrap' },
            {
              key: 'source',
              header: t('journal.source'),
              render: (row) => <SourceBadge source={mapDocTypeToSource(row.documentType)} />,
              className: 'whitespace-nowrap',
            },
            { key: 'content', header: t('common.description'), className: 'max-w-48 truncate' },
            {
              key: 'totalDebit',
              header: t('common.amount'),
              render: (row) => <span className="num">{formatMoney(row.totalDebit)}</span>,
              className: 'text-right',
            },
            {
              key: 'status',
              header: t('common.status'),
              render: () => (
                <span className="inline-flex items-center gap-1 text-xs text-green-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  {t('journal.posted')}
                </span>
              ),
              className: 'whitespace-nowrap',
            },
          ]}
          data={entries.slice(0, 50)}
          keyFn={(row) => row.id}
          emptyMessage={t('journal.noPostingLogEntries')}
        />
      </div>
    </div>
  );
}

